import { useState, useEffect, useCallback, useRef } from "react";

const PAGE = 120;
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, X, ChevronLeft, ChevronRight, Share2, Check, AlertCircle, CalendarDays, Images, CheckSquare } from "lucide-react";
import { API, mediaUrl, triggerDownload } from "@/lib/galleryApi";
import { AccessGate } from "@/components/gallery/AccessGate";
import { ZipPartsDialog } from "@/components/gallery/ZipPartsDialog";
import { RetryImg } from "@/components/gallery/RetryImg";

export function PublicLightbox({ photos, index, setIndex, canDownload }) {
  const p = photos[index];
  const nav = useCallback((d) => setIndex((i) => Math.max(0, Math.min(photos.length - 1, i + d))), [photos.length, setIndex]);
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") setIndex(null); if (e.key === "ArrowLeft") nav(-1); if (e.key === "ArrowRight") nav(1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [nav, setIndex]);
  const [tx, setTx] = useState(null);
  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="public-lightbox"
      onTouchStart={(e) => setTx(e.touches[0].clientX)} onTouchEnd={(e) => { if (tx !== null) { const d = e.changedTouches[0].clientX - tx; if (Math.abs(d) > 50) nav(d < 0 ? 1 : -1); } setTx(null); }}>
      <div className="flex items-center justify-between p-4 text-white/70 text-xs">
        <span>{index + 1} / {photos.length}</span>
        <button onClick={() => setIndex(null)} className="hover:text-white" data-testid="public-lightbox-close"><X className="w-6 h-6" /></button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center relative px-2 sm:px-14">
        <RetryImg key={p.id} src={mediaUrl(p.web_url)} className="max-w-full max-h-full object-contain" />
        {index > 0 && <button onClick={() => nav(-1)} className="hidden sm:flex absolute left-3 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center text-white" data-testid="public-lightbox-prev"><ChevronLeft /></button>}
        {index < photos.length - 1 && <button onClick={() => nav(1)} className="hidden sm:flex absolute right-3 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center text-white" data-testid="public-lightbox-next"><ChevronRight /></button>}
      </div>
      {canDownload && p.download_url && (
        <div className="p-4 flex justify-center">
          <Button onClick={() => triggerDownload(mediaUrl(p.download_url))} className="bg-white text-black hover:bg-white/90 h-11 px-6" data-testid="public-lightbox-download"><Download className="w-4 h-4 mr-2" />Telecharger en haute qualite</Button>
        </div>
      )}
    </div>
  );
}

export default function PublicGalleryPage() {
  const { shareToken } = useParams();
  const storeKey = `gallery_access_${shareToken}`;
  const [access, setAccess] = useState(() => sessionStorage.getItem(storeKey));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [zipParts, setZipParts] = useState(null);
  const [limit, setLimit] = useState(PAGE);
  const sentinel = useRef(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setLimit((l) => l + PAGE), { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  });

  useEffect(() => {
    axios.get(`${API}/api/public/gallery/${shareToken}`, { params: access ? { access } : {} })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.status === 410 ? "Cette galerie a expire." : "Galerie introuvable, privee ou desactivee."));
  }, [shareToken, access]);

  const grant = (a) => { sessionStorage.setItem(storeKey, a); setAccess(a); };
  const perms = data?.permissions || {};
  const photos = data?.photos || [];

  const zip = async (ids) => {
    try {
      const r = await axios.post(`${API}/api/public/gallery/${shareToken}/zip`, { access, photo_ids: ids }, { timeout: 20000 });
      if (r.data.parts.length > 1) return setZipParts(r.data);
      triggerDownload(mediaUrl(r.data.url));
      toast.success(`Telechargement de ${r.data.count} photo(s) en cours…`);
    } catch (e) { toast.error(e.response?.data?.detail || "Telechargement impossible"); }
  };
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: data.title, url }); } catch { /* dismissed */ } }
    else { navigator.clipboard.writeText(url); toast.success("Lien copie"); }
  };

  if (error) return (
    <div className="min-h-screen bg-[#05080F] flex items-center justify-center px-6 text-center" data-testid="public-gallery-error">
      <div><AlertCircle className="w-10 h-10 text-[#64748B] mx-auto mb-4" /><p className="text-white">{error}</p></div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#05080F] flex items-center justify-center"><div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#05080F] text-white" data-testid="public-gallery-page">
      <header className="relative overflow-hidden">
        {data.cover_url && <img src={mediaUrl(data.cover_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35 scale-105 blur-[2px]" />}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-[#05080F]/70 to-[#05080F]" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-10 sm:pt-24 sm:pb-14">
          {data.org_logo && <img src={data.org_logo} alt="" className="h-9 mb-6 object-contain" />}
          <p className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-3">{data.org_name || "Galerie photo"}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl" data-testid="public-gallery-title">{data.title}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-white/60">
            {data.event_title && <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{data.event_title}</span>}
            {!data.locked && <span className="flex items-center gap-1.5" data-testid="public-photo-count"><Images className="w-4 h-4" />{photos.length} photos</span>}
          </div>
          {data.description && <p className="text-white/70 text-sm sm:text-base mt-4 max-w-2xl">{data.description}</p>}
          {!data.locked && (
            <div className="flex flex-wrap gap-2 mt-8">
              {perms.allow_download_zip && photos.length > 0 && <Button onClick={() => zip(null)} className="bg-white text-black hover:bg-white/90 h-11 px-5" data-testid="download-zip-btn"><Download className="w-4 h-4 mr-2" />Tout telecharger (ZIP)</Button>}
              {(perms.allow_download_single || perms.allow_download_zip) && photos.length > 0 && (
                <Button variant="outline" onClick={() => { setSelecting((s) => !s); setSelected(new Set()); }} className="h-11 bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="toggle-select-mode"><CheckSquare className="w-4 h-4 mr-2" />{selecting ? "Annuler la selection" : "Selectionner"}</Button>
              )}
              {perms.allow_share && <Button variant="outline" onClick={share} className="h-11 bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="share-btn"><Share2 className="w-4 h-4 mr-2" />Partager</Button>}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-8 pb-24">
        {data.locked ? (
          <AccessGate token={shareToken} meta={data} onAccess={grant} />
        ) : photos.length === 0 ? (
          <p className="text-center text-white/50 py-20" data-testid="public-empty">Les photos seront bientot disponibles.</p>
        ) : (
          <div className="columns-2 sm:columns-3 lg:columns-4 gap-2 sm:gap-3 [&>*]:mb-2 sm:[&>*]:mb-3" data-testid="public-photo-grid">
            {photos.slice(0, limit).map((p, i) => {
              const sel = selected.has(p.id);
              return (
                <button key={p.id} className={`relative block w-full break-inside-avoid overflow-hidden rounded-md bg-white/5 group ${sel ? "ring-2 ring-[#0099CC]" : ""}`} data-testid={`public-photo-${p.id}`}
                  style={{ aspectRatio: p.width && p.height ? `${p.width}/${p.height}` : undefined }}
                  onClick={() => (selecting ? setSelected((s) => { const n = new Set(s); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n; }) : setView(i))}>
                  <RetryImg src={mediaUrl(p.thumb_url)} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  {selecting && <span className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center ${sel ? "bg-[#0099CC] border-[#0099CC]" : "border-white bg-black/30"}`}>{sel && <Check className="w-3.5 h-3.5" />}</span>}
                </button>
              );
            })}
          </div>
        )}
      </main>

      {!data.locked && photos.length > limit && <div ref={sentinel} className="h-10" data-testid="public-load-more" />}
      {selecting && selected.size > 0 && (
        <div className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 z-40 bg-white text-black rounded-full shadow-2xl px-5 py-2.5 flex items-center gap-4" data-testid="public-selection-bar">
          <span className="text-sm font-medium">{selected.size} photo(s)</span>
          <Button onClick={() => zip([...selected])} className="h-9 rounded-full bg-black text-white hover:bg-black/80" data-testid="download-selection-btn"><Download className="w-4 h-4 mr-2" />Telecharger</Button>
        </div>
      )}
      <footer className="border-t border-white/5 py-6 text-center text-[11px] text-white/30">Galerie propulsee par <span className="text-white/60">GLOTECH EVENT</span></footer>
      <ZipPartsDialog zip={zipParts} onClose={() => setZipParts(null)} light />
      {view !== null && <PublicLightbox photos={photos} index={view} setIndex={setView} canDownload={perms.allow_download_single} />}
    </div>
  );
}
